<template>
	<component
		:is="rootComponent"
		class="ov-btn"
		:class="rootClass"
		v-bind="rootBind"
		@click="onClick"
	>
		<slot />
	</component>
</template>

<script>
import { RouterLink } from "vue-router";

function resolveRoot({ to, href, disabled } = {}) {
	if (disabled) {
		return "button";
	}
	if (to != null && to !== false && to !== "") {
		return "RouterLink";
	}
	if (href) {
		return "a";
	}
	return "button";
}

function resolveRel({ target, rel } = {}) {
	if (rel != null && rel !== "") {
		return rel;
	}
	if (target === "_blank") {
		return "noopener noreferrer";
	}
	return undefined;
}

export default {
	name: "OvButton",
	props: {
		variant: { type: String, default: "primary" },
		size: { type: String, default: "md" },
		type: { type: String, default: "button" },
		disabled: { type: Boolean, default: false },
		/** Vue Router location — renders RouterLink when set (and not disabled). */
		to: { type: [String, Object], default: null },
		/** External or plain URL — renders <a> when set without `to`. */
		href: { type: String, default: null },
		target: { type: String, default: null },
		rel: { type: String, default: null },
	},
	emits: ["click"],
	computed: {
		rootKind() {
			return resolveRoot({
				to: this.to,
				href: this.href,
				disabled: this.disabled,
			});
		},
		rootComponent() {
			if (this.rootKind === "RouterLink") {
				return RouterLink;
			}
			return this.rootKind;
		},
		rootClass() {
			return [
				`ov-btn--${this.variant}`,
				`ov-btn--${this.size}`,
				{ "ov-btn--disabled": this.disabled },
			];
		},
		resolvedRel() {
			return resolveRel({ target: this.target, rel: this.rel });
		},
		rootBind() {
			if (this.rootKind === "RouterLink") {
				const bind = { to: this.to };
				if (this.target) {
					bind.target = this.target;
				}
				if (this.resolvedRel) {
					bind.rel = this.resolvedRel;
				}
				return bind;
			}
			if (this.rootKind === "a") {
				const bind = { href: this.href };
				if (this.target) {
					bind.target = this.target;
				}
				if (this.resolvedRel) {
					bind.rel = this.resolvedRel;
				}
				return bind;
			}
			return {
				type: this.type,
				disabled: this.disabled,
			};
		},
	},
	methods: {
		onClick(e) {
			if (this.disabled) {
				e.preventDefault();
				e.stopPropagation();
				return;
			}
			this.$emit("click", e);
		},
	},
};
</script>

<style lang="scss" scoped>
.ov-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: var(--ds-space-2);
	font-family: inherit;
	font-weight: var(--ds-font-weight-medium);
	border: 1px solid transparent;
	border-radius: var(--ds-radius-md);
	cursor: pointer;
	text-decoration: none;
	transition: background 0.15s, color 0.15s, border-color 0.15s, box-shadow 0.15s;

	&--md {
		height: var(--ds-control-height);
		padding: 0 var(--ds-space-4);
		font-size: var(--ds-font-size-sm);
	}

	&--sm {
		height: var(--ds-control-height-sm);
		padding: 0 var(--ds-space-3);
		font-size: var(--ds-font-size-xs);

		.ov-busy-spinner {
			width: var(--ds-icon-size-md);
			height: var(--ds-icon-size-md);
		}
	}

	&--primary {
		background: var(--ds-color-primary);
		color: var(--ds-color-primary-text);

		&:hover:not(:disabled):not(.ov-btn--disabled) {
			background: var(--ds-color-primary-hover);
			color: var(--ds-color-primary-text);
		}
	}

	&--secondary {
		background: var(--ds-color-surface);
		color: var(--ds-color-text);
		border-color: var(--ds-color-border);

		&:hover:not(:disabled):not(.ov-btn--disabled) {
			background: var(--ds-color-bg);
			border-color: #cbd5e1;
			color: var(--ds-color-text);
		}
	}

	&--ghost {
		background: transparent;
		color: var(--ds-color-text-muted);

		&:hover:not(:disabled):not(.ov-btn--disabled) {
			background: var(--ds-color-bg);
			color: var(--ds-color-text);
		}
	}

	&--disabled,
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
		pointer-events: none;
	}
}
</style>
